import { getApiBaseUrl } from './api';

const BATCH_FLUSH_INTERVAL_MS = 3000;
const MAX_BATCH_SIZE = 10;
const PAGE_VIEW_DEDUPE_WINDOW_MS = 10000; // 10 seconds deduplication for page views

const eventQueue = [];
let flushTimer = null;

let lastPageView = {
  path: '',
  timestamp: 0,
};

export const ROUTE_NAMES = {
  '/product-dashboard': 'Platform Health Dashboard',
  '/product-security': 'Product Security Monitoring',
  '/platform-health': 'Platform Health Dashboard',
  '/overview': 'Admin Overview',
  '/my-teams': 'My Teams Workspace',
  '/screenshots': 'Screenshots Monitoring',
  '/timelapse-videos': 'Timelapse Recordings',
  '/timesheet': 'Employee Timesheets',
  '/timeline': 'Activity Timeline',
  '/manual-time': 'Manual Time Approval',
  '/reports': 'Reports Overview',
  '/reports/daily-attendance': 'Daily Attendance Report',
  '/reports/monthly-attendance': 'Monthly Attendance Report',
  '/reports/monthly-in-out': 'Monthly In/Out Report',
  '/reports/late-clock-in': 'Late Clock-in Report',
  '/reports/over-time-report': 'Overtime Analysis Report',
  '/reports/custom-report': 'Custom Report Generator',
  '/reports/apps-usage': 'Apps Usage Analytics',
  '/reports/work-activity-log': 'Work Activity Log',
  '/reports/work-load-analysis': 'Workload Analysis Report',
  '/reports/shift-time': 'Shift Time Distribution',
  '/reports/summary-report': 'Summary Executive Report',
  '/payroll': 'Payroll Management',
  '/real-time-alert': 'Real-Time Risk Alerts',
  '/apply-leave': 'Leave Applications',
  '/leave-summary': 'Leave Balances Summary',
  '/manage-leave': 'Leave Management',
  '/manage-holiday': 'Holiday Calendar',
  '/review-usage': 'App Category Review',
  '/settings': 'Organization Settings',
  '/billing': 'Subscription & Billing',
  '/downloads': 'Desktop App Downloads',
  '/account-settings': 'Account Settings',
  '/application-logs': 'Production Logs Explorer',
  '/user/my-work': 'User Dashboard - My Work',
  '/user/screenshots': 'User Dashboard - Screenshots',
  '/user/timelapse-videos': 'User Dashboard - Timelapses',
  '/user/timesheet': 'User Dashboard - Timesheet',
  '/user/manual-time': 'User Dashboard - Manual Time',
  '/user/apply-leave': 'User Dashboard - Apply Leave',
  '/user/account-settings': 'User Dashboard - Account Settings',
};

export const getFriendlyRouteName = (pathname) => {
  if (!pathname) return 'Unknown View';
  if (ROUTE_NAMES[pathname]) return ROUTE_NAMES[pathname];

  if (pathname.startsWith('/member/')) return 'User Detail Profile';
  if (pathname.startsWith('/reports/')) return 'Analytics Report';
  if (pathname.startsWith('/payroll/')) return 'Payroll Section';
  if (pathname.startsWith('/settings/')) return 'System Settings';

  const cleaned = pathname.replace(/^\//, '').replace(/-/g, ' ');
  return cleaned ? cleaned.charAt(0).toUpperCase() + cleaned.slice(1) : 'Home Page';
};

const getAuthToken = () => {
  try {
    return localStorage.getItem('token') || sessionStorage.getItem('token') || '';
  } catch (_) {
    return '';
  }
};

const sendBatch = async (items) => {
  if (!items || items.length === 0) return;
  const token = getAuthToken();
  if (!token) return;

  const endpoint = `${getApiBaseUrl().replace(/\/$/, '')}/user-events/`;

  try {
    await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      keepalive: true,
      body: JSON.stringify(items),
    });
  } catch (_) {
    // Fail silently
  }
};

export const flushUserEvents = () => {
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  if (eventQueue.length === 0) return;

  const batch = eventQueue.splice(0, 50);
  sendBatch(batch);
};

const scheduleFlush = () => {
  if (flushTimer) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    flushUserEvents();
  }, BATCH_FLUSH_INTERVAL_MS);
};

export const trackAction = (
  action,
  category = 'navigation',
  description = '',
  metadata = {},
  source = 'web'
) => {
  if (typeof window === 'undefined') return;

  const currentPath = window.location ? `${window.location.pathname}${window.location.search}` : '';

  const payload = {
    action,
    category,
    source,
    description: description || action,
    metadata: {
      path: currentPath,
      friendly_page: getFriendlyRouteName(window.location.pathname),
      window_size: `${window.innerWidth}x${window.innerHeight}`,
      screen_size: window.screen ? `${window.screen.width}x${window.screen.height}` : '',
      timestamp: new Date().toISOString(),
      ...(metadata && typeof metadata === 'object' ? metadata : {}),
    },
  };

  eventQueue.push(payload);

  if (eventQueue.length >= MAX_BATCH_SIZE) {
    flushUserEvents();
  } else {
    scheduleFlush();
  }
};

export const trackPageView = (pathname, title = '') => {
  if (typeof window === 'undefined') return;
  const now = Date.now();

  // Deduplicate identical route page view events within 10 seconds window
  if (lastPageView.path === pathname && now - lastPageView.timestamp < PAGE_VIEW_DEDUPE_WINDOW_MS) {
    return;
  }

  const timeSpentSeconds = lastPageView.timestamp ? Math.round((now - lastPageView.timestamp) / 1000) : 0;
  const friendlyName = getFriendlyRouteName(pathname);

  lastPageView = {
    path: pathname,
    timestamp: now,
  };

  trackAction('page_view', 'navigation', `Viewed ${friendlyName}`, {
    pathname,
    friendly_name: friendlyName,
    time_spent_on_previous_page: timeSpentSeconds > 0 ? `${timeSpentSeconds}s` : undefined,
    page_title: typeof document !== 'undefined' ? document.title : title,
  });
};

if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    flushUserEvents();
  });
}

let appClickBuffer = [];
let appFlushTimer = null;

const flushAppClicks = async () => {
  if (!appClickBuffer.length) return;
  const token = getAuthToken();
  if (!token) return;

  const eventsToSend = [...appClickBuffer];
  appClickBuffer = [];

  const endpoint = `${getApiBaseUrl().replace(/\/$/, '')}/user-click-events/`;

  try {
    const res = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      keepalive: true,
      body: JSON.stringify(eventsToSend),
    });
    if (res.status === 401) {
      appClickBuffer = [];
    }
  } catch (_) {
    // Fail silently
  }
};

export const initAppClickTracker = () => {
  if (typeof window === 'undefined') return () => {};

  const handleClick = (e) => {
    try {
      const pathname = window.location.pathname || '';
      // Private workspaces can contain contact details and bearer share links.
      if (e.target?.closest?.('[data-private]')) return;
      if (pathname === '/' || pathname === '/landing' || pathname === '/login' || pathname === '/signup' || pathname === '/forgot-password') {
        return;
      }

      const token = getAuthToken();
      if (!token) return; // Only track for authenticated users

      const docEl = document.documentElement || document.body;
      const scrollWidth = Math.max(docEl.scrollWidth || 1, window.innerWidth || 1);
      const scrollHeight = Math.max(docEl.scrollHeight || 1, window.innerHeight || 1);

      const pageX = e.pageX || (e.clientX + window.scrollX);
      const pageY = e.pageY || (e.clientY + window.scrollY);

      const xPct = Math.round((pageX / scrollWidth) * 10000) / 100;
      const yPct = Math.round((pageY / scrollHeight) * 10000) / 100;
      const yPx = Math.round(pageY);

      const target = e.target;
      const interactiveEl = target.closest
        ? target.closest('a, button, input, select, textarea, tr, [role="button"], .ph-card, header, sidebar') || target
        : target;

      const elementText = String(
        interactiveEl.innerText || interactiveEl.value || interactiveEl.alt || interactiveEl.title || ''
      )
        .trim()
        .replace(/\s+/g, ' ')
        .substring(0, 150);

      const tag = interactiveEl.tagName ? interactiveEl.tagName.toLowerCase() : '';
      const id = interactiveEl.id ? `#${interactiveEl.id}` : '';
      const className = interactiveEl.className && typeof interactiveEl.className === 'string'
        ? `.${interactiveEl.className.trim().split(/\s+/)[0]}`
        : '';
      const selector = `${tag}${id}${className}`.substring(0, 200);

      const ua = (navigator.userAgent || '').toLowerCase();
      let deviceType = 'desktop';
      if (/mobile|iphone|ipod|android|blackberry|opera mini|iemobile/.test(ua)) deviceType = 'mobile';
      else if (/ipad|tablet|playbook|silk/.test(ua)) deviceType = 'tablet';

      const clickEvent = {
        path: window.location.pathname || '/overview',
        x_pct: Math.min(Math.max(xPct, 0), 100),
        y_pct: Math.min(Math.max(yPct, 0), 100),
        y_px: yPx,
        viewport_width: window.innerWidth || 0,
        viewport_height: window.innerHeight || 0,
        device_type: deviceType,
        element_tag: tag,
        element_id: interactiveEl.id || '',
        element_text: elementText,
        element_selector: selector,
      };

      appClickBuffer.push(clickEvent);
      flushAppClicks();
    } catch (_) {
      // Ignore
    }
  };

  window.addEventListener('click', handleClick, { capture: true, passive: true });

  const handleUnload = () => {
    flushAppClicks();
  };
  window.addEventListener('beforeunload', handleUnload);

  return () => {
    window.removeEventListener('click', handleClick, { capture: true });
    window.removeEventListener('beforeunload', handleUnload);
  };
};

const userTrackingService = {
  trackAction,
  trackPageView,
  flushUserEvents,
  initAppClickTracker,
  getFriendlyRouteName,
  ROUTE_NAMES,
};

export default userTrackingService;
